import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Control, FieldPath, useController, useFieldArray, useFormContext } from 'react-hook-form';

import CurrencySymbol from '~/components/common/CurrencySymbol';
import type { ContractFormValues } from '~/components/contracts/types';
import { ContractForm } from '~/components/contracts/types';
import { LAISelect, LAITextField } from '~/components/form';
import { BUTTON_SX } from '~/theme';

let rateSetId = 0;

const getNewRate = (): ContractForm['rateSets'][number]['rates'][number] => ({
  description: '',
  workType: '',
  workerType: '',
  rateType: '',
  hourlyRate: 0,
  coverageType: 'billable',
  isDefault: false,
});

const getDefaultRateSetForm = (): ContractForm['rateSets'][number] => {
  return {
    id: `new:${rateSetId++}`,
    isNew: true,
    name: '',
    description: '',
    rates: [
      {
        ...getNewRate(),
        rateType: 'regular',
        workerType: 'mechanic',
        workType: 'labor',
      },
    ],
  };
};

const yesNoOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const coverageTypeOptions = [
  { label: 'Fully Covered', value: 'fully-covered' },
  { label: 'Premium Only', value: 'premium-only' },
  { label: 'Billable', value: 'billable' },
];

type SelectFieldProps = {
  name: FieldPath<ContractFormValues>;
  control: Control<ContractFormValues>;
  options: { label: string; value: string }[];
};

const SelectField = ({ name, control, options }: SelectFieldProps) => {
  const { field } = useController({ name, control });

  return (
    <LAISelect
      name={name}
      hookFormRef={field.ref}
      value={field.value}
      onChange={field.onChange}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      options={options}
    />
  );
};

type ContractRateSetFormProps = {
  control: Control<ContractFormValues>;
  name: `rateSets.${number}`;
  sx?: React.ComponentProps<typeof Box>['sx'];
};

const ContractRateSetForm = ({ control, sx, name }: ContractRateSetFormProps) => {
  const fieldPath = `${name}.rates` as const;
  const { fields, append, remove } = useFieldArray({ name: fieldPath, control });

  return (
    <Box sx={{ ...sx, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
      <Box display="flex" gap={2} mb={2}>
        <LAITextField
          label="Rate Set Name"
          fullWidth
          type="text"
          control={control}
          name={`${name}.name`}
        />
        <LAITextField
          label="Description"
          fullWidth
          type="text"
          control={control}
          name={`${name}.description`}
        />
      </Box>

      <Typography variant="subtitle1" mt={2}>
        Rates
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Rate Type</TableCell>
              <TableCell>Worker Type</TableCell>
              <TableCell>Work Type</TableCell>
              <TableCell align="right">Hourly Rate</TableCell>
              <TableCell>Coverage Type</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <LAITextField
                    control={control}
                    name={`${name}.rates.${index}.rateType`}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <LAITextField
                    control={control}
                    name={`${name}.rates.${index}.workerType`}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <LAITextField
                    control={control}
                    name={`${name}.rates.${index}.workType`}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <LAITextField
                    control={control}
                    name={`${name}.rates.${index}.hourlyRate`}
                    type="number"
                    size="small"
                    slotProps={{
                      htmlInput: { min: 0, step: 0.01 },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencySymbol />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ width: '100px' }}
                  />
                </TableCell>
                <TableCell>
                  <SelectField
                    name={`${name}.rates.${index}.coverageType`}
                    control={control}
                    options={coverageTypeOptions}
                  />
                </TableCell>
                <TableCell>
                  <SelectField
                    name={`${name}.rates.${index}.isDefault`}
                    control={control}
                    options={yesNoOptions}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(index)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        type="button"
        variant="outlined"
        startIcon={<Add />}
        onClick={() => append(getNewRate())}
        sx={{ ...BUTTON_SX, mb: 2 }}
      >
        ADD RATE
      </Button>
    </Box>
  );
};

export const ContractRateSetsFields = () => {
  const { control } = useFormContext<ContractFormValues>();

  const { fields, insert } = useFieldArray({
    control: control,
    name: 'rateSets',
    shouldUnregister: false,
  });

  const addRateSet = () => {
    const newRateSet = getDefaultRateSetForm();
    insert(0, newRateSet);
  };

  return (
    <Box p={2} sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          startIcon={
            <Add
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                padding: '2px',
              }}
            />
          }
          onClick={addRateSet}
          sx={BUTTON_SX}
        >
          Add Rate Set
        </Button>
      </Box>

      {fields.map((field, index) => (
        <ContractRateSetForm
          key={field.id}
          control={control}
          sx={{ mt: index > 0 ? 4 : 0 }}
          name={`rateSets.${index}`}
        />
      ))}
    </Box>
  );
};
